import React, { useMemo, useState } from 'react';
import useSWR from 'swr';
import { AlertTriangle, CheckCircle2, ChevronDown, FileKey, Loader2, Plus, RefreshCw, Search, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useAppState } from '../app/appContext';
import { useZonesList } from '@/hooks/useCurrentZone';
import { refreshZoneFeatures } from '@/hooks/useZoneFeatures';
import { extractApiError } from '@/utils/apiError';
import {
  createReceiptConnection,
  deleteReceiptConnection,
  listReceiptConnections,
  receiptConnectionsKey,
  syncReceiptConnection,
  updateReceiptConnection,
  getReceiptProducts,
  receiptProductsKey,
  setReceiptProductCategories,
  type ReceiptConnectionDto,
  type ReceiptProductsDto,
  type SabySecrets,
} from '@/api/receipts';
import { legalEntitiesKey, listLegalEntities } from '@/api/legalEntities';
import { Tooltip } from '@/components/ui/tooltip';

// «Чеки» (Алексей 22.10, #1941): подключение к ОФД — пока один провайдер, Saby (СБИС).
// Параметры подключения только вводятся: сервер хранит их зашифрованными и обратно не отдаёт.
// Подключение = юрлицо (Алексей 22.10, #1985), не вся сеть: видят только настраивающие его филиалы (#2003). После подключения кассы сами сопоставляются с точками по названию; несопоставленную —
// выбрать вручную. Чеки выгружаются каждые 5 минут, первый раз — за последние 30 дней.

const PROVIDERS = [{ value: 'saby' as const, label: 'Saby (СБИС) ОФД' }];

const fmtDateTime = (s: string | null) =>
  s ? new Date(s).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';

function SecretFields({ value, onChange }: { value: SabySecrets; onChange: (v: SabySecrets) => void }) {
  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) onChange({ ...value, secret_key: (await f.text()).trim() });
    e.target.value = '';
  };
  return (
    <div className="grid gap-3">
      <label className="grid gap-1 text-sm">
        <span className="font-medium">ID подключения</span>
        <Input value={value.app_client_id} onChange={e => onChange({ ...value, app_client_id: e.target.value })} autoComplete="off" />
      </label>
      <label className="grid gap-1 text-sm">
        <span className="font-medium">Защищённый ключ</span>
        <Input type="password" value={value.app_secret} onChange={e => onChange({ ...value, app_secret: e.target.value })} autoComplete="new-password" />
      </label>
      <div className="grid gap-1 text-sm">
        <span className="font-medium">Сервисный ключ (файл .key)</span>
        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm hover:bg-muted">
            <FileKey className="size-4" /> Выбрать файл
            <input type="file" className="hidden" onChange={onFile} />
          </label>
          {value.secret_key && <span className="text-xs text-success">Ключ загружен</span>}
        </div>
        <Textarea
          rows={2}
          value={value.secret_key}
          onChange={e => onChange({ ...value, secret_key: e.target.value })}
          placeholder="…или вставьте содержимое файла"
          className="font-mono text-xs"
          style={{ WebkitTextSecurity: 'disc' } as React.CSSProperties}
        />
      </div>
    </div>
  );
}

const EMPTY: SabySecrets = { app_client_id: '', app_secret: '', secret_key: '' };

function AddConnection({ onDone }: { onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [leId, setLeId] = useState('');
  const { data: les } = useSWR(open ? legalEntitiesKey() : null, () => listLegalEntities().then(r => r.data.legal_entities));
  const [secrets, setSecrets] = useState<SabySecrets>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ready = leId && secrets.app_client_id.trim() && secrets.app_secret.trim() && secrets.secret_key.trim();
  if (!open) {
    return (
      <Button onClick={() => setOpen(true)} className="w-fit"><Plus className="mr-1.5 size-4" />Добавить подключение</Button>
    );
  }
  const submit = async () => {
    setBusy(true); setError(null);
    try {
      await createReceiptConnection({ legal_entity_id: Number(leId), provider: 'saby', name, secrets });
      setSecrets(EMPTY); setName(''); setOpen(false); onDone();
    } catch (e) {
      setError(extractApiError(e, 'Не удалось подключиться'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Новое подключение</CardTitle>
        <CardDescription>Параметры выдаёт Saby в разделе интеграций. После сохранения посмотреть их здесь нельзя.</CardDescription>
      </CardHeader>
      <CardContent className="grid max-w-xl gap-3">
        <label className="grid gap-1 text-sm">
          <span className="font-medium">Юрлицо</span>
          <Select className="h-9 text-sm" value={leId} onChange={e => setLeId(e.target.value)}>
            <option value="">— выберите —</option>
            {(les ?? []).map(le => <option key={le.id} value={le.id}>{le.name}{le.inn ? ` · ИНН ${le.inn}` : ''} — {le.group.name}</option>)}
          </Select>
          {les && les.length === 0 && (
            <span className="text-xs text-muted-foreground">Юрлиц пока нет — их добавляют в «Система → Группы» у группы филиалов владельца.</span>
          )}
        </label>
        <label className="grid gap-1 text-sm">
          <span className="font-medium">Провайдер</span>
          <Select className="h-9 text-sm" value="saby" onChange={() => undefined}>
            {PROVIDERS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </Select>
        </label>
        <label className="grid gap-1 text-sm">
          <span className="font-medium">Название <span className="font-normal text-muted-foreground">(необязательно)</span></span>
          <Input value={name} onChange={e => setName(e.target.value)} placeholder="Подставится юрлицо из Saby" />
        </label>
        <SecretFields value={secrets} onChange={setSecrets} />
        {error && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex gap-2">
          <Button onClick={submit} disabled={!ready || busy}>{busy && <Loader2 className="mr-1.5 size-4 animate-spin" />}Подключить</Button>
          <Button variant="ghost" onClick={() => { setOpen(false); setError(null); }}>Отмена</Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Товары по стеллажам (Алексей 22.10, #1941): позиция из чеков → тип стеллажа. Сначала раскладывает
// модель, здесь — поправить. Оценка потерь сопоставит просадку стеллажа с продажами его товаров.
// Порядок позиций (Алексей #2048): похожие рядом — все сиропы вместе, молоко вместе и т. п.;
// добавки «- …» (с минуса) отдельным блоком после обычных позиций. Группы и позиции внутри —
// по продажам за 30 дней.
const PRODUCT_GROUPS = ['сироп', 'молок', 'основа', 'шот', 'сосиск', 'комбо', 'хот-дог', 'хот дог', 'лимонад', 'чай', 'каша'];
const productGroup = (name: string) => {
  const n = name.replace(/^[-–\s]+/, '').toLowerCase();
  return PRODUCT_GROUPS.find(g => n.includes(g))?.replace('хот дог', 'хот-дог') ?? n.split(/\s+/)[0];
};
function sortProducts<P extends { name: string; qty_30d: number }>(products: P[]): P[] {
  const keyOf = (p: P) => `${/^\s*[-–]/.test(p.name) ? 1 : 0}:${productGroup(p.name)}`;
  const groupQty = new Map<string, number>();
  for (const p of products) groupQty.set(keyOf(p), (groupQty.get(keyOf(p)) ?? 0) + p.qty_30d);
  return [...products].sort((a, b) => {
    const ka = keyOf(a), kb = keyOf(b);
    if (ka !== kb) return ka[0] !== kb[0] ? ka[0].localeCompare(kb[0]) : (groupQty.get(kb)! - groupQty.get(ka)!) || ka.localeCompare(kb);
    return b.qty_30d - a.qty_30d || a.name.localeCompare(b.name, 'ru');
  });
}

function ProductCategories({ connId, canEdit }: { connId: number; canEdit: boolean }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState<string | null>(null);
  const { data, mutate, isLoading } = useSWR<ReceiptProductsDto>(
    open ? receiptProductsKey(connId) : null,
    async () => (await getReceiptProducts(connId)).data,
    { revalidateOnFocus: false },
  );
  const counts = useMemo(() => {
    const m: Record<string, number> = {};
    for (const p of data?.products ?? []) m[p.category || ''] = (m[p.category || ''] ?? 0) + 1;
    return m;
  }, [data]);
  const sorted = useMemo(() => sortProducts(data?.products ?? []), [data]);
  const shown = sorted.filter(p =>
    (cat === null || (p.category || '') === cat) && (!q || p.name.toLowerCase().includes(q.toLowerCase())));
  const setOne = async (name: string, category: string) => {
    const r = await setReceiptProductCategories(connId, [{ name, category }]);
    void mutate(r.data, { revalidate: false });
  };
  return (
    <div className="rounded-lg border">
      <button type="button" onClick={() => setOpen(v => !v)} className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm font-medium">
        <span>Товары по стеллажам <span className="font-normal text-muted-foreground">— что из чеков лежит на каком типе стеллажа</span></span>
        <ChevronDown className={`size-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="grid gap-2 border-t p-3">
          {isLoading && !data ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="size-4 animate-spin" />Загрузка…</div>
          ) : (data?.products.length ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground">Позиции появятся после первой выгрузки чеков — модель разложит их по стеллажам сама.</p>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-1.5">
                <button type="button" onClick={() => setCat(null)} className={`rounded-full border px-2.5 py-0.5 text-xs ${cat === null ? 'border-primary bg-primary/10 text-primary' : ''}`}>
                  Все · {data?.products.length}
                </button>
                {[...(data?.categories ?? []), ...(counts[''] ? [{ key: '', label: 'Без категории', rack_type: null }] : [])].map(c => (
                  <button key={c.key || 'none'} type="button" onClick={() => setCat(c.key)} className={`rounded-full border px-2.5 py-0.5 text-xs ${cat === c.key ? 'border-primary bg-primary/10 text-primary' : ''}`}>
                    {c.label} · {counts[c.key] ?? 0}
                  </button>
                ))}
                <label className="ml-auto flex items-center gap-1.5 rounded-md border px-2">
                  <Search className="size-3.5 text-muted-foreground" />
                  <input value={q} onChange={e => setQ(e.target.value)} placeholder="Поиск" className="h-7 w-40 bg-transparent text-sm outline-none" />
                </label>
              </div>
              <div className="max-h-[28rem] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-card">
                    <tr className="text-left text-xs text-muted-foreground">
                      <th className="py-1.5 pr-3 font-medium">Позиция</th>
                      <th className="py-1.5 pr-3 text-right font-medium">Продано за 30 дн.</th>
                      <th className="py-1.5 font-medium">Стеллаж</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map(p => (
                      <tr key={p.name} className="border-t">
                        <td className="py-1.5 pr-3">{p.name}</td>
                        <td className="py-1.5 pr-3 text-right tabular-nums text-muted-foreground">{Math.round(p.qty_30d).toLocaleString('ru-RU')}</td>
                        <td className="py-1.5">
                          <Select
                            className="h-7 text-sm" wrapperClassName="w-auto inline-flex"
                            value={p.category}
                            disabled={!canEdit}
                            onChange={e => void setOne(p.name, e.target.value)}
                          >
                            {!p.category && <option value="">—</option>}
                            {(data?.categories ?? []).map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                          </Select>
                          {p.source === 'auto' && p.category && <Tooltip content="Разложено автоматически"><span className="ml-1.5 text-xs text-muted-foreground">авто</span></Tooltip>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function ConnectionCard({ conn, canEdit, onChanged }: { conn: ReceiptConnectionDto; canEdit: boolean; onChanged: () => void }) {
  const { data: zones } = useZonesList();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rekey, setRekey] = useState(false);
  const [secrets, setSecrets] = useState<SabySecrets>(EMPTY);
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true); setError(null);
    try { await fn(); onChanged(); } catch (e) { setError(extractApiError(e, 'Ошибка')); } finally { setBusy(false); }
  };
  const ok = !conn.last_error;
  return (
    <Card>
      <CardHeader className="gap-1">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-base">
            {conn.legal_entity?.name || conn.name || 'Saby'} {conn.inn && <span className="font-normal text-muted-foreground">· ИНН {conn.inn}</span>}
            {conn.legal_entity && <span className="ml-1 text-sm font-normal text-muted-foreground">· группа {conn.legal_entity.group.name}</span>}
          </CardTitle>
          {canEdit && (
            <div className="flex gap-1.5">
              <Button size="sm" variant="outline" disabled={busy} onClick={() => run(() => syncReceiptConnection(conn.id))}>
                <RefreshCw className="mr-1.5 size-3.5" />Выгрузить сейчас
              </Button>
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => setRekey(v => !v)}>Сменить ключи</Button>
              <Button size="sm" variant="ghost" disabled={busy} className="text-destructive"
                onClick={() => { if (window.confirm('Удалить подключение и все выгруженные чеки?')) void run(() => deleteReceiptConnection(conn.id)); }}>
                <Trash2 className="size-4" />
              </Button>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            {ok ? <CheckCircle2 className="size-4 text-success" /> : <AlertTriangle className="size-4 text-destructive" />}
            {ok ? 'Подключено' : conn.last_error}
          </span>
          <span>Выгрузка: {fmtDateTime(conn.last_sync_at)}</span>
          <span>Чеков: {conn.receipts.toLocaleString('ru-RU')}</span>
          <span>Ключи: {conn.has_secrets ? 'заданы' : 'не заданы'}</span>
        </div>
      </CardHeader>
      <CardContent className="grid gap-3">
        {rekey && canEdit && (
          <div className="grid max-w-xl gap-3 rounded-lg border p-3">
            <SecretFields value={secrets} onChange={setSecrets} />
            <Button className="w-fit" disabled={busy || !(secrets.app_client_id && secrets.app_secret && secrets.secret_key)}
              onClick={() => run(async () => { await updateReceiptConnection(conn.id, { secrets }); setSecrets(EMPTY); setRekey(false); })}>
              Сохранить ключи
            </Button>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground">
                <th className="py-1.5 pr-3 font-medium">Касса</th>
                <th className="py-1.5 pr-3 font-medium">Точка</th>
                <th className="py-1.5 font-medium">Чеки до</th>
              </tr>
            </thead>
            <tbody>
              {conn.registers.map(r => (
                <tr key={r.id} className="border-t">
                  <td className="py-2 pr-3">
                    <div>{r.sales_point || r.reg_id}</div>
                    <div className="text-xs text-muted-foreground">{r.model} · РН {r.reg_id}</div>
                  </td>
                  <td className="py-2 pr-3">
                    <Select
                      className="h-8 text-sm" wrapperClassName="max-w-[16rem]"
                      value={r.zone_id ?? ''}
                      disabled={!canEdit || busy}
                      onChange={e => {
                        const zid = e.target.value ? Number(e.target.value) : null;
                        if (r.zone_id && !window.confirm('Перенести кассу? Её чеки выгрузятся заново под новой точкой.')) return;
                        void run(() => updateReceiptConnection(conn.id, { registers: [{ id: r.id, zone_id: zid }] }));
                      }}
                    >
                      <option value="">— не выгружать —</option>
                      {(zones ?? []).filter(z => !z.is_archived || String(z.id) === String(r.zone_id)).map(z => (
                        <option key={z.id} value={z.id}>{z.humanreadable_name}</option>
                      ))}
                    </Select>
                  </td>
                  <td className="py-2 text-muted-foreground">{fmtDateTime(r.synced_until)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <ProductCategories connId={conn.id} canEdit={canEdit} />
        {error && <p className="text-sm text-destructive">{error}</p>}
      </CardContent>
    </Card>
  );
}

export default function ReceiptsSettings() {
  const { zone } = useAppState();
  const zoneId = zone.selectedZoneId;
  const { data: resp, isLoading, mutate } = useSWR(
    receiptConnectionsKey,
    async () => (await listReceiptConnections()).data,
    { refreshInterval: 30_000 },
  );
  const data = resp?.connections;
  const canEdit = !!resp?.can_add;
  const here = (data ?? [])
    .map(c => ({ name: c.name || 'Saby', count: c.registers.filter(r => String(r.zone_id) === String(zoneId)).length }))
    .filter(h => h.count > 0);
  const changed = () => { void mutate(); if (zoneId) refreshZoneFeatures(String(zoneId)); };
  return (
    <div className="mx-auto grid w-full max-w-4xl gap-4">
      {isLoading && !data ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="size-4 animate-spin" />Загрузка…</div>
      ) : (
        <>
          {here.length > 0 && (
            <p className="text-sm">
              Кассы этого филиала ({here.reduce((n, h) => n + h.count, 0)}) подключены через{' '}
              {here.map(h => `«${h.name}»`).join(', ')}.
            </p>
          )}
          <p className="text-sm text-muted-foreground">
            Подключение — к юрлицу из группы филиалов владельца: одно на все его точки, кассы раскладываются по
            филиалам ниже. Видят его только администраторы этой группы.
          </p>
          {(data ?? []).length === 0 && (
            <p className="text-sm text-muted-foreground">
              Подключите ОФД — чеки точки будут подтягиваться автоматически: количество, средний чек, что продаётся, кто пробил.
            </p>
          )}
          {(data ?? []).map(c => <ConnectionCard key={c.id} conn={c} canEdit={canEdit} onChanged={changed} />)}
          {canEdit && <AddConnection onDone={changed} />}
        </>
      )}
    </div>
  );
}
